import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const signals = [
  { label: 'Hire / No-hire decisions', icon: 'UserCheck' },
  { label: 'Stage pass rates', icon: 'BarChart3' },
  { label: 'Scorecard patterns', icon: 'ScanSearch' },
  { label: 'Role-specific rubrics', icon: 'SlidersHorizontal' },
];

const BusinessLearningSection = () => {
  return (
    <section
      id="learning"
      className="relative border-t border-[var(--lp-line)] bg-[var(--lp-contrast-bg)] py-24 text-[var(--lp-contrast-text)] lg:py-32"
    >
      <div className="lp-grain absolute inset-0 opacity-[0.06]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-mono text-[11px] font-semibold tracking-[0.2em] text-[var(--lp-accent)]">
              Adaptive intelligence
            </p>
            <h2 className="mt-4 text-4xl leading-[1.05] tracking-tight text-[var(--lp-contrast-text)] sm:text-5xl">
              We learn from your
              <span className="block text-[var(--lp-contrast-text)]/50">hiring decisions.</span>
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-[var(--lp-contrast-text)]/55">
              Every hire and rejection trains your model. prepxai refines screening criteria, interview focus, and scoring weights so each cycle surfaces stronger candidates — aligned with how your team actually hires.
            </p>

            <ul className="mt-8 space-y-3">
              {[
                'Gets smarter with every hiring cycle',
                'Surfaces traits your best hires share',
                'Reduces false positives in early rounds',
                'Keeps human judgment in the final call',
              ].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-[var(--lp-contrast-text)]/70">
                  <Icon name="Check" size={14} className="shrink-0 text-[var(--lp-accent)]" strokeWidth={3} />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="relative"
          >
            <div className="rounded-2xl border border-[var(--lp-contrast-text)]/10 bg-[var(--lp-contrast-text)]/5 p-8 backdrop-blur">
              <div className="flex items-center justify-between border-b border-[var(--lp-contrast-text)]/10 pb-6">
                <span className="font-mono text-[10px] tracking-widest text-[var(--lp-accent)]">
                  Feedback loop
                </span>
                <motion.span
                  className="flex h-2 w-2 rounded-full bg-emerald-400"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                />
              </div>

              <div className="mt-6 space-y-4">
                {signals.map((signal, i) => (
                  <motion.div
                    key={signal.label}
                    initial={{ opacity: 0, x: 12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + i * 0.08 }}
                    className="flex items-center gap-4 rounded-xl border border-[var(--lp-contrast-text)]/10 bg-[var(--lp-contrast-text)]/[0.03] px-4 py-3"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--lp-accent)]/15">
                      <Icon name={signal.icon} size={16} className="text-[var(--lp-accent)]" />
                    </div>
                    <span className="text-sm text-[var(--lp-contrast-text)]/80">{signal.label}</span>
                    <motion.div
                      className="ml-auto h-1.5 w-16 overflow-hidden rounded-full bg-[var(--lp-contrast-text)]/10"
                    >
                      <motion.div
                        className="h-full rounded-full bg-[var(--lp-accent)]"
                        initial={{ width: '20%' }}
                        whileInView={{ width: `${55 + i * 10}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 + i * 0.1 }}
                      />
                    </motion.div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-center gap-3 rounded-xl border border-dashed border-[var(--lp-accent)]/30 bg-[var(--lp-accent)]/5 px-4 py-5">
                <Icon name="Sparkles" size={18} className="text-[var(--lp-accent)]" />
                <p className="text-center text-sm text-[var(--lp-contrast-text)]/70">
                  Next pipeline run uses updated scoring — better matches, fewer wasted interviews.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default BusinessLearningSection;
